import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styles from '../styles/EditableField.module.css';

const EditableField = ({
  label,
  value,
  name,
  type = 'text',
  onSave,
  validation,
  helperText
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    setTempValue(value);
  }, [value]);

  const formatPhoneNumber = (input) => {
    const cleaned = input.replace(/\D/g, '');
    let formatted = cleaned;

    if (cleaned.length > 0) {
      if (cleaned.length <= 3) {
        formatted = `(${cleaned}`;
      } else if (cleaned.length <= 6) {
        formatted = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
      } else {
        formatted = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
      }
    }
    return formatted;
  };

  const handleEdit = () => {
    setIsEditing(true);
    setTempValue(value);
    setConfirmPassword('');
    setError(null);
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    if (type === 'tel') {
      setTempValue(formatPhoneNumber(newValue));
    } else {
      setTempValue(newValue);
    }
  };

  const handleSave = () => {
    if (type === 'password') {
      if (tempValue !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }
      if (!tempValue) {
        setError('Password is required');
        return;
      }
    }

    if (validation) {
      const validationError = validation(tempValue);
      if (validationError) {
        setError(validationError);
        return;
      }
    }

    onSave(name, tempValue);
    setIsEditing(false);
    setError(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setTempValue(value);
    setConfirmPassword('');
    setError(null);
  };

  const getInputProps = (isPasswordField = false) => {
    const baseProps = {
      onChange: handleChange,
      className: styles.input,
      value: isPasswordField ? confirmPassword : tempValue,
    };

    if (type === 'password' || type === 'email') {
      return {
        ...baseProps,
        autoComplete: 'new-password',
        autoCorrect: 'off',
        autoCapitalize: 'off',
        spellCheck: 'false',
        'data-form-type': type,
        'data-lpignore': 'true',
        'data-form-autofill': 'off',
        name: `${name}_${Math.random().toString(36).substr(2, 9)}`
      };
    }

    return {
      ...baseProps,
      autoComplete: 'on',
      name: name,
      type: type
    };
  };

  const renderInput = () => {
    if (type === 'textarea') {
      return (
        <textarea
          {...getInputProps()}
          className={`${styles.input} ${styles.textarea}`}
          rows={4}
          placeholder="Enter your notes here..."
        />
      );
    }

    if (type === 'password' && isEditing) {
      return (
        <div className={styles.passwordFields}>
          <input
            {...getInputProps()}
            type="password"
            placeholder="Enter new password"
          />
          <input
            {...getInputProps(true)}
            type="password"
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm new password"
          />
        </div>
      );
    }

    if (type === 'tel') {
      return (
        <input
          {...getInputProps()}
          type="text"
          inputMode="numeric"
          placeholder="(555) 555-5555"
          maxLength={14}
        />
      );
    }

    return <input {...getInputProps()} />;
  };

  const renderFieldValue = () => {
    if (type === 'password') {
      return <span className={styles.value}>••••••••</span>;
    }
    if (type === 'textarea') {
      return (
        <pre className={`${styles.value} ${styles.textareaValue}`}>
          {value || 'No notes added yet'}
        </pre>
      );
    }
    return <span className={styles.value}>{value || ''}</span>;
  };

  return (
    <div className={styles.fieldContainer}>
      <label className={styles.fieldLabel}>{label}</label>
      <div className={styles.fieldContent}>
        {isEditing ? (
          <div className={styles.editMode}>
            {renderInput()}
            <div className={styles.buttonGroup}>
              <button onClick={handleSave} className={styles.saveButton}>
                Save
              </button>
              <button onClick={handleCancel} className={styles.cancelButton}>
                Cancel
              </button>
            </div>
            {error && <span className={styles.error}>{error}</span>}
          </div>
        ) : (
          <div className={styles.displayMode}>
            {renderFieldValue()}
            <button
              onClick={handleEdit}
              className={styles.editButton}
              aria-label={`Edit ${label}`}
            >
              Edit
            </button>
          </div>
        )}
      </div>
      {helperText && <span className={styles.helperText}>{helperText}</span>}
    </div>
  );
};

EditableField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  type: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  validation: PropTypes.func,
  helperText: PropTypes.string,
};

export default EditableField;
